import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../../config';

const BASE_URL = config.apiUrl;
const CONVERSATIONS_PER_PAGE = 20;

interface Conversation {
  id: number;
  uuid: string;
  title: string | null;
  created_at?: string;
  updated_at?: string;
  context_type?: string | null;
  active_run_status?: 'running' | 'just_done' | null;
}

export const useConversations = () => {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [hasMoreConversations, setHasMoreConversations] = useState(true);
  const [conversationPage, setConversationPage] = useState(1);

  const fetchConversations = useCallback(async (page = 1) => {
    const token = localStorage.getItem('accessToken');
    const tokenType = localStorage.getItem('tokenType');
    if (!token) {
      navigate('/login');
      return;
    }

    // Only show loading state on initial load
    if (page === 1) {
      setIsLoadingConversations(true);
    }

    try {
      const skip = (page - 1) * CONVERSATIONS_PER_PAGE;
      const response = await fetch(
        `${BASE_URL}/conversations/?skip=${skip}&limit=${CONVERSATIONS_PER_PAGE}`, 
        {
          headers: {
            'Authorization': `${tokenType} ${token}`
          }
        }
      );

      // Handle 401 Unauthorized explicitly
      if (response.status === 401) {
        console.log('401 Unauthorized, redirecting to login');
        localStorage.clear(); // Clear all stored data
        navigate('/login');
        return;
      }

      if (!response.ok) {
        // Set hasMore to false to prevent further attempts
        setHasMoreConversations(false);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data: Conversation[] = await response.json();
      
      // Reset list if it's first page, otherwise append
      if (page === 1) {
        setConversations(data);
      } else {
        setConversations(prev => {
          const existingIds = new Set(prev.map(conv => conv.id));
          const newConversations = data.filter(conv => !existingIds.has(conv.id));
          return [...prev, ...newConversations];
        });
      }

      setHasMoreConversations(data.length === CONVERSATIONS_PER_PAGE);
      return data;
    } catch (error) {
      console.error('Error fetching conversations:', error);
      setHasMoreConversations(false);
      return [];
    } finally {
      // Only clear loading state on initial load
      if (page === 1) {
        setIsLoadingConversations(false);
      }
    }
  }, [navigate]);

  const loadMoreConversations = useCallback(() => {
    if (hasMoreConversations && !isLoadingConversations) {
      const nextPage = conversationPage + 1;
      setConversationPage(nextPage);
      fetchConversations(nextPage);
    }
  }, [hasMoreConversations, isLoadingConversations, conversationPage, fetchConversations]);

  return {
    conversations,
    setConversations,
    isLoadingConversations,
    hasMoreConversations,
    conversationPage,
    setConversationPage,
    fetchConversations,
    loadMoreConversations
  };
};